import { CustomGamesResourceCache, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import {
  APP_SETTINGS_SERVICE_TOKEN,
  CheatComponent,
  CHEAT_COMPONENT_TOKEN,
  CustomLoader,
  CustomReelsConfigComponent,
  DynamicDrawOrdersProvider,
  ElementsStateController,
  FreeSpinsModeProvider,
  GAME_STATE_MACHINE_TOKEN,
  FAST_SPINS_COMPONENT,
  GameConfigController,
  GameProvider,
  ICON_VALUES_SYSTEMS_TOKEN,
  LongStoppingIconEnumeratorProvider,
  MultiSceneIconResourceProvider,
  ORIENTATION_HANDLER_COMPONENT_TOKEN,
  REQUIRED_COMPONENTS_PROVIDER_TOKEN,
  ResponseInitialReelsComponent,
  SingleSceneIconStopBehaviourProvider,
  SingleSceneIconStopBehaviourWithFSSupportProvider,
  SLOT_GAME_CONFIG_MANAGER_TOKEN,
  SLOT_GAME_SETTINGS_TOKEN,
  SlotBonusGameConfigManager,
  SlotPrimaryAnimationWOEndFSAnimationProvider,
  SlotSoundConfigEntry,
  SlotSoundType,
  SoundConfigurationProvider,
  T_AbstractIconResourceProvider,
  T_AbstractIconStopbehaviorProvider,
  T_AnticipationAnimationProvider,
  T_DynamicDrawOrdersProvider,
  T_ElementsStateController,
  T_EndFreeSpinsPopupComponent,
  T_EpicWinPopupProvider,
  T_GameConfigController,
  T_IconEnumeratorComponent,
  T_IFreeSpinsModeProvider,
  T_InitialReelsComponent,
  T_IReelsConfigProvider,
  T_ISlotGameEngineProvider,
  T_ISlotPrimaryAnimationProvider,
  T_MultiSceneIconWithValuesProvider,
  T_OptionalLoader,
  T_StartFreeSpinsPopupComponent,
  T_SubstituteIconComponent,
  T_WinLineActionComponent,
  FastSpinsComponent,
  CUSTOM_COMPONENTS_PROVIDER_TOKEN,
  PAYTABLE_POPUP_COMPONENT_TOKEN,
  HELP_POPUP_COMPONENT_TOKEN,
  BUY_FEATURE_POPUP_COMPONENT_TOKEN,
} from '@cgs/machines';
import {
  ApplicationGameConfig,
  LOCALIZATION_MANAGER_TOKEN,
  LocalizationManager,
} from '@cgs/shared';
import { InstanceLifetime, SoundState, T_ResourceCache } from '@cgs/wge';
import { corsicaGameSettings, CorsicaGameSettings } from './corsica-game-settings';

import { CorsicaBonusGameProvider } from './slot/bonus/corsica-bonus-game-provider';
import {
  CORSICA_BACKGROUND_COMPONENT_TOKEN,
  CorsicaBackgroundComponent,
} from './slot/corsica-background.component';
import { CorsicaEpicWinPopupProvider } from './slot/epic-win/corsica-epic-win-popup.provider';
import { CorsicaGameStateMachine } from './slot/corsica-game-state-machine';
import {
  T_BaseSlotSoundController,
  T_BonusGameProvider,
  T_IconsSceneObjectComponent,
  T_IStartSlotActionProvider,
  T_IStopSlotActionProvider,
  T_SingleSceneIconStopBehaviourProvider,
  T_SoundConfigurationProvider,
  IconsSceneObjectWithScissorComponent,
} from '@cgs/machines';
import {
  TrifectaGameHelp,
  TRIFECTA_LAUNCH_PARAMS_TOKEN,
  TrifectaLaunchParamsDto,
  TrifectaNumberFormatter,
} from '@cgs/provider-trifecta';
import { CorsicaAppSettingsService } from './slot/app-settings/corsica-app-settings.service';
import { CorsicaBaseSlotSoundController } from './slot/base-slot-sound/corsica-base-slot-sound.controller';
import { CorsicaBuyFeaturePopup } from './slot/buy-feature/corsica-buy-feature-popup';
import { CorsicaEndFreeSpinsPopupComponent } from './slot/free-spins-popups/end-free-spins-popup/corsica-end-free-spins-popup.component';
import { CorsicaStartFreeSpinsPopupComponent } from './slot/free-spins-popups/start-free-spins-popup/corsica-start-free-spins-popup.component';
import { CORSICA_FREE_SPINS_COUNT_COMPONENT_TOKEN } from './slot/free-spins/corsica-free-spins-count.component';
import {
  CORSICA_FREE_SPINS_END_COMPONENT_TOKEN,
  CorsicaFreeSpinsEndComponent,
} from './slot/free-spins/corsica-free-spins-end.component';
import {
  CORSICA_FREE_SPINS_START_COMPONENT_TOKEN,
  CorsicaFreeSpinsStartComponent,
} from './slot/free-spins/corsica-free-spins-start.component';
import {
  CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN,
  CorsicaFreeSpinsTotalComponent,
} from './slot/free-spins/corsica-free-spins-total.component';
import {
  CORSICA_FREE_SPINS_UPDATE_SYMBOLS_COMPONENT_TOKEN,
  CorsicaFreeSpinsUpdateSymbolsComponent,
} from './slot/free-spins/corsica-free-spins-update-symbols.component';
import { CorsicaIconValuesSystems } from './slot/free-spins/corsica-icon-with-values/corsica-icon-values-systems';
import { CorsicaIconWithValuesProvider } from './slot/free-spins/corsica-icon-with-values/corsica-icon-with-values.provider';
import { CorsicaGameRules } from './slot/hud/corsica-game-rules';
import { CorsicaReelsEngineComponent } from './slot/reel/corsica-reels-engine.component';
import { CorsicaOrientationHandlerComponent } from './slot/resize-handler/corsica-orientation-handler.component';
import {
  CORSICA_BONUS_TRIGGER_COMPONENT,
  CorsicaBonusTriggerComponent,
} from './slot/spin/corsica-bonus-trigger.component';
import { CorsicaSpinActionComponent } from './slot/spin/corsica-spin-action.component';
import { CorsicaStopActionComponent } from './slot/spin/stop-action/corisca-stop-action.component';
import { CorsicaWinLineActionComponent } from './slot/win-line/corsica-winline-action.component';
import {
  CORSICA_WIN_LINE_POPUP_TOKEN,
  CorsicaWinLinePopup,
} from './slot/win-line/corsica-winline-popup';

export class CorsicaGameProvider extends GameProvider {
  public override register(): void {
    super.register();

    this.container.register(
      LOCALIZATION_MANAGER_TOKEN,
      () => {
        const launchParams = this.container.resolve<TrifectaLaunchParamsDto>(
          TRIFECTA_LAUNCH_PARAMS_TOKEN
        );
        const localizationManager = new LocalizationManager(
          this.container,
          launchParams.languageId
        );
        void localizationManager.init();
        return localizationManager;
      },
      {
        lifetime: InstanceLifetime.Singleton,
      }
    );

    this.registerOverride();

    this.container.registerValue(
      REQUIRED_COMPONENTS_PROVIDER_TOKEN,
      [
        T_GameConfigController,
        ORIENTATION_HANDLER_COMPONENT_TOKEN,
        T_StartFreeSpinsPopupComponent,
        T_AnticipationAnimationProvider,
        T_SubstituteIconComponent,
        T_InitialReelsComponent,
        T_BonusGameProvider,
        T_SingleSceneIconStopBehaviourProvider,

        CORSICA_BONUS_TRIGGER_COMPONENT,

        CORSICA_BACKGROUND_COMPONENT_TOKEN,
        CORSICA_FREE_SPINS_END_COMPONENT_TOKEN,

        CORSICA_FREE_SPINS_COUNT_COMPONENT_TOKEN,
        CORSICA_FREE_SPINS_UPDATE_SYMBOLS_COMPONENT_TOKEN,

        CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN,

        CORSICA_FREE_SPINS_START_COMPONENT_TOKEN,
        CORSICA_WIN_LINE_POPUP_TOKEN,

        T_EpicWinPopupProvider,
        T_EndFreeSpinsPopupComponent,
        T_MultiSceneIconWithValuesProvider,
      ],
      { multi: true }
    );
  }

  private registerOverride(): void {
    this.container.register(CUSTOM_COMPONENTS_PROVIDER_TOKEN, () => {
      this.container.register(
        NUMBER_FORMATTER_TOKEN,
        (container) => new TrifectaNumberFormatter(container),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        T_AbstractIconResourceProvider,
        (container) => new MultiSceneIconResourceProvider(container, 'icons', 'icons'),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_ISlotGameEngineProvider,
        (c) =>
          new CorsicaReelsEngineComponent(
            c,
            true,
            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 16, 17, 18, 19, 20],
            'alpha'
          ),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(T_IReelsConfigProvider, (c) => new CustomReelsConfigComponent(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(T_GameConfigController, (c) => new GameConfigController(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(
        ORIENTATION_HANDLER_COMPONENT_TOKEN,
        (c) => new CorsicaOrientationHandlerComponent(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_WinLineActionComponent,
        (c) => new CorsicaWinLineActionComponent(c),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        T_IconEnumeratorComponent,
        (c) => new LongStoppingIconEnumeratorProvider(c, []),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        GAME_STATE_MACHINE_TOKEN,
        (c) => new CorsicaGameStateMachine(c).initialize(),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        T_DynamicDrawOrdersProvider,
        (c) => new DynamicDrawOrdersProvider(c),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        T_ElementsStateController,
        (c) => new ElementsStateController(c, ElementsStateController.defaultStates('reel_bg')),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_ISlotPrimaryAnimationProvider,
        (c) => new SlotPrimaryAnimationWOEndFSAnimationProvider(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_InitialReelsComponent,
        (container) => new ResponseInitialReelsComponent(container),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(T_EpicWinPopupProvider, (c) => new CorsicaEpicWinPopupProvider(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(
        T_AbstractIconStopbehaviorProvider,
        (c) => new SingleSceneIconStopBehaviourWithFSSupportProvider(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_IFreeSpinsModeProvider,
        (c) => new FreeSpinsModeProvider(c, 'reel_bg', '{0}', ['freeGame', 'free'], null, '', []),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_StartFreeSpinsPopupComponent,
        (c) => new CorsicaStartFreeSpinsPopupComponent(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_BaseSlotSoundController,
        (c) => {
          return new CorsicaBaseSlotSoundController(c, [
            new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Playing, 'show'),
            new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Stopped, 'hide'),
            new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Paused, 'pause'),
          ]);
        },
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        SLOT_GAME_SETTINGS_TOKEN,
        () => {
          const resourceUrl =
            this.container
              .resolve<CustomGamesResourceCache>(T_ResourceCache)
              .getUrl(`games/${ApplicationGameConfig.gameId}`) || '';
          return new CorsicaGameSettings(resourceUrl, corsicaGameSettings);
        },
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(CHEAT_COMPONENT_TOKEN, (c) => new CheatComponent(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(
        T_EndFreeSpinsPopupComponent,
        (c) =>
          new CorsicaEndFreeSpinsPopupComponent(c, {
            stopBackgroundSound: true,
            useFreeSpinMode: true,
            closeOnAnimFinished: true,
            closeButtonsName: 'btnContinue',
          }),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        CORSICA_BACKGROUND_COMPONENT_TOKEN,
        (c) => new CorsicaBackgroundComponent(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        CORSICA_BONUS_TRIGGER_COMPONENT,
        (c) => new CorsicaBonusTriggerComponent(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_IStartSlotActionProvider,
        (c) => new CorsicaSpinActionComponent(c, true),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        CORSICA_FREE_SPINS_START_COMPONENT_TOKEN,
        (c) => new CorsicaFreeSpinsStartComponent(c),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );
      this.container.register(PAYTABLE_POPUP_COMPONENT_TOKEN, (c) => new CorsicaGameRules(c), {
        lifetime: InstanceLifetime.Singleton,
      });
      this.container.register(HELP_POPUP_COMPONENT_TOKEN, (c) => new TrifectaGameHelp(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(
        BUY_FEATURE_POPUP_COMPONENT_TOKEN,
        (c) => new CorsicaBuyFeaturePopup(c),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        T_SoundConfigurationProvider,
        () =>
          new SoundConfigurationProvider({
            stopBackgroundSoundOnAddFreeSpins: false,
            stopBackgroundSoundOnEndScatter: false,
            stopBackgroundSoundOnBonus: true,
            stopBackgroundSoundOnStartFreeSpins: true,
          }),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        CORSICA_FREE_SPINS_END_COMPONENT_TOKEN,
        (c) => new CorsicaFreeSpinsEndComponent(c),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        T_IconsSceneObjectComponent,
        (c) => new IconsSceneObjectWithScissorComponent(c),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        T_SingleSceneIconStopBehaviourProvider,
        (c) => new SingleSceneIconStopBehaviourProvider(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(T_IStopSlotActionProvider, (c) => new CorsicaStopActionComponent(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(
        T_BonusGameProvider,
        (container) =>
          new CorsicaBonusGameProvider(container, {
            hideSlot: true,
            hideHud: false,
            gameTypes: ['Default'],
            externalScenesIds: ['game'],
            gameTypeToBonusId: new Map<string, string>([['Default', 'rules']]),
            hasFreeSpins: true,
            fsTypes: [],
          }),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        SLOT_GAME_CONFIG_MANAGER_TOKEN,
        (c) => new SlotBonusGameConfigManager(c, ['rules']),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN,
        (c) => new CorsicaFreeSpinsTotalComponent(c),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      this.container.register(
        CORSICA_FREE_SPINS_UPDATE_SYMBOLS_COMPONENT_TOKEN,
        (c) => new CorsicaFreeSpinsUpdateSymbolsComponent(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(
        T_MultiSceneIconWithValuesProvider,
        (c) => new CorsicaIconWithValuesProvider(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(CORSICA_WIN_LINE_POPUP_TOKEN, (c) => new CorsicaWinLinePopup(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(ICON_VALUES_SYSTEMS_TOKEN, (c) => new CorsicaIconValuesSystems(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(APP_SETTINGS_SERVICE_TOKEN, (c) => new CorsicaAppSettingsService(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      this.container.register(
        FAST_SPINS_COMPONENT,
        (container) => new FastSpinsComponent(container, 1.5),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      // Preload 'loading' so the start-page scenes (loading/scene,
      // loading/sceneLogo) are cached before the loader finishes — otherwise
      // the slot flashes before the start page appears.
      this.container.register(
        T_OptionalLoader,
        (container) => new CustomLoader(container, ['loading']),
        { lifetime: InstanceLifetime.Singleton }
      );
    });
  }
}
